/**
 * apps/battle/client/src/terminal/screens/PracticeMenu.tsx
 *
 * Phase 4.2 M6 — Practice menu.
 *
 * Listed AFTER a student lands on a cleared cluster from the title screen
 * (Option α: PRACTICE_MENU first, then question screen). Shows every cleared
 * cluster with its current XP headroom so students can revise across the
 * subject, not just the cluster they entered from.
 *
 * Routing UX:
 *   title → (selected cleared cluster) → practice-menu (this screen)
 *                                       → practice-question (QuestionScreen mode='practice')
 */
import { useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '../../api.js';
import { TerminalFrame } from '../components/TerminalFrame.js';
import { KeyBar, type KeyBarItem } from '../components/KeyBar.js';
import { useKeyboard, type KeyHandler } from '../hooks/useKeyboard.js';

export interface PracticeCluster {
  clusterId: string;
  title: string;
  world: number | null;
  templateCount: number;
  maxXp: number;
  earnedXp: number;
  headroomXp: number;
}

// Phase 4.2 M14 — a fully-solved portal eligible for portal practice.
export interface PracticePortal {
  portalKey: string;
  tier: number;
  templateCount: number;
  maxXp: number;
  earnedXp: number;
  headroomXp: number;
}

interface PracticeEligibleResp {
  clusters: PracticeCluster[];
}

interface PracticePortalEligibleResp {
  portals: PracticePortal[];
}

/** Human label for a portal tier (mark value). */
function portalTierLabel(tier: number): string {
  if (tier === 12) return 'final';
  if (tier === 9) return 'conditional';
  return 'analyse';
}

interface Props {
  /**
   * Cluster the student selected from TitleScreen. The menu pre-highlights it
   * but the student can still pick any other cleared cluster.
   */
  initialClusterId: string;
  /**
   * M14-patch1 — portal the student selected from TitleScreen. The menu
   * pre-highlights its row in CLEARED PORTALS. Optional: absent for
   * cluster-origin entries (default highlight behaviour unchanged).
   */
  initialPortalKey?: string;
  onPickCluster: (cluster: PracticeCluster) => void;
  onPickPortal: (portal: PracticePortal) => void;
  onBackToTitle: () => void;
}

export function PracticeMenu({ initialClusterId, initialPortalKey, onPickCluster, onPickPortal, onBackToTitle }: Props) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clusters, setClusters] = useState<PracticeCluster[]>([]);
  const [portals, setPortals] = useState<PracticePortal[]>([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // M14 — portal-practice eligibility loaded alongside clusters. A portal
        // failure must not blank the (existing) cluster menu, so it's tolerated.
        const [clusterResp, portalResp] = await Promise.all([
          api.get<PracticeEligibleResp>('/api/practice/eligible'),
          api.get<PracticePortalEligibleResp>('/api/practice/portal/eligible').catch(() => ({ portals: [] })),
        ]);
        if (!alive) return;
        setClusters(clusterResp.clusters ?? []);
        setPortals(portalResp.portals ?? []);
        setLoading(false);
      } catch (err) {
        if (!alive) return;
        setError(err instanceof ApiError ? err.message : 'Failed to load practice menu.');
        setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Sort by world then clusterId so the menu is stable across renders.
  const sorted = useMemo(() => {
    return [...clusters].sort((a, b) => {
      const wa = a.world ?? 99;
      const wb = b.world ?? 99;
      if (wa !== wb) return wa - wb;
      return a.clusterId.localeCompare(b.clusterId);
    });
  }, [clusters]);

  // M14 — portals sorted by tier (desc) then key for a stable order.
  const sortedPortals = useMemo(() => {
    return [...portals].sort((a, b) => {
      if (a.tier !== b.tier) return b.tier - a.tier;
      return a.portalKey.localeCompare(b.portalKey);
    });
  }, [portals]);

  // Keyboard: each cluster's letter selects it. Portals use number keys 1-9
  // (portal keys are multi-char "X-Y" so they can't be a single keypress, and
  // numbers avoid colliding with cluster letters). [B] back to title.
  const handlers: KeyHandler[] = useMemo(() => {
    const hs: KeyHandler[] = [{ key: 'b', handler: onBackToTitle }];
    for (const c of sorted) {
      hs.push({ key: c.clusterId, handler: () => onPickCluster(c) });
    }
    sortedPortals.slice(0, 9).forEach((p, i) => {
      hs.push({ key: String(i + 1), handler: () => onPickPortal(p) });
    });
    return hs;
  }, [sorted, sortedPortals, onPickCluster, onPickPortal, onBackToTitle]);
  useKeyboard(handlers);

  const keybar: KeyBarItem[] = [];
  if (sorted.length > 0) {
    keybar.push({
      label: sorted.map((c) => c.clusterId).join('/'),
      description: 'enter cluster',
    });
  }
  if (sortedPortals.length > 0) {
    keybar.push({
      label: sortedPortals.slice(0, 9).map((_, i) => i + 1).join('/'),
      description: 'enter portal',
    });
  }
  keybar.push({ label: 'B', description: 'back to title', onActivate: onBackToTitle });

  const titleNode = (
    <>
      ECONQUEST <span className="tm-text--dim">{'\u2500'}</span>{' '}
      <span className="tm-feedback-correct">PRACTICE MENU</span>
    </>
  );

  if (loading) {
    return (
      <TerminalFrame title={titleNode} keybar={<KeyBar items={keybar} />}>
        <div className="tm-loading">{'>'} loading cleared clusters…</div>
      </TerminalFrame>
    );
  }

  if (error) {
    return (
      <TerminalFrame title={titleNode} keybar={<KeyBar items={keybar} />}>
        <div className="tm-section">
          <div className="tm-text tm-text--warn">{'>'} {error}</div>
        </div>
      </TerminalFrame>
    );
  }

  if (sorted.length === 0 && sortedPortals.length === 0) {
    return (
      <TerminalFrame title={titleNode} keybar={<KeyBar items={keybar} />}>
        <div className="tm-section">
          <div className="tm-text">
            {'>'} Nothing cleared yet. Clear a cluster or solve a portal in normal battle to unlock practice mode.
          </div>
        </div>
      </TerminalFrame>
    );
  }

  return (
    <TerminalFrame title={titleNode} keybar={<KeyBar items={keybar} />}>
      <div className="tm-section">
        <div className="tm-prompt">{'>'}</div> Practice — earn the remaining XP, revise concepts, or just sharpen reflexes.
      </div>

      {sorted.length > 0 && (
        <div className="tm-section">
          <div className="tm-h1 tm-feedback-correct">CLEARED CLUSTERS</div>
          {sorted.map((c) => (
            <PracticeRow
              key={c.clusterId}
              cluster={c}
              highlighted={c.clusterId === initialClusterId}
              onEnter={() => onPickCluster(c)}
            />
          ))}
        </div>
      )}

      {sortedPortals.length > 0 && (
        <div className="tm-section">
          <div className="tm-h1 tm-feedback-correct">CLEARED PORTALS</div>
          {sortedPortals.map((p, i) => (
            <PortalPracticeRow
              key={p.portalKey}
              portal={p}
              highlighted={p.portalKey === initialPortalKey}
              hotkey={i < 9 ? String(i + 1) : null}
              onEnter={() => onPickPortal(p)}
            />
          ))}
        </div>
      )}

      <div className="tm-section tm-text--dim" style={{ fontSize: '0.85em' }}>
        Practice does not unlock new content. XP only tops up unclaimed headroom — you can't farm
        beyond the maximum a cluster or portal awards — but persistence badges (e.g. Drill Sergeant) can still unlock.
      </div>
    </TerminalFrame>
  );
}

function PracticeRow({
  cluster,
  highlighted,
  onEnter,
}: {
  cluster: PracticeCluster;
  highlighted: boolean;
  onEnter: () => void;
}) {
  const max = Math.max(1, cluster.maxXp);
  const pct = Math.min(100, Math.round((cluster.earnedXp / max) * 100));
  // Discrete 20-cell bar (looks crisp in monospace and gives clear thresholds).
  const cells = 20;
  const filled = Math.round((pct / 100) * cells);
  const bar = '█'.repeat(filled) + '░'.repeat(Math.max(0, cells - filled));
  const cls = 'tm-cluster-row' + (highlighted ? ' tm-cluster-row--cleared' : '');
  return (
    <div className={cls}>
      <button type="button" className="tm-cluster-row__hit" onClick={onEnter}>
        <span className="tm-cluster-row__key">[{cluster.clusterId}]</span>
        <span className="tm-cluster-row__name">{cluster.title || cluster.clusterId}</span>
        <span className="tm-cluster-row__status">
          <span className="tm-text--dim" style={{ fontFamily: 'inherit' }}>{bar}</span>{' '}
          {cluster.earnedXp}/{cluster.maxXp} XP{' '}
          <span className="tm-text--dim">·</span>{' '}
          {cluster.headroomXp > 0
            ? <span className="tm-feedback-correct">+{cluster.headroomXp} headroom</span>
            : <span className="tm-text--dim">mastered</span>}
        </span>
      </button>
    </div>
  );
}

// Phase 4.2 M14 — a single solved-portal practice row. Mirrors PracticeRow but
// keyed by portal_key (X-Y) with a number hotkey and a tier label.
function PortalPracticeRow({
  portal,
  highlighted,
  hotkey,
  onEnter,
}: {
  portal: PracticePortal;
  highlighted: boolean;
  hotkey: string | null;
  onEnter: () => void;
}) {
  const max = Math.max(1, portal.maxXp);
  const pct = Math.min(100, Math.round((portal.earnedXp / max) * 100));
  const cells = 20;
  const filled = Math.round((pct / 100) * cells);
  const bar = '█'.repeat(filled) + '░'.repeat(Math.max(0, cells - filled));
  const cls = 'tm-cluster-row' + (highlighted ? ' tm-cluster-row--cleared' : '');
  return (
    <div className={cls}>
      <button type="button" className="tm-cluster-row__hit" onClick={onEnter}>
        <span className="tm-cluster-row__key">[{hotkey ?? portal.portalKey}]</span>
        <span className="tm-cluster-row__name">
          {portal.portalKey} <span className="tm-text--dim">·</span> {portalTierLabel(portal.tier)}
        </span>
        <span className="tm-cluster-row__status">
          <span className="tm-text--dim" style={{ fontFamily: 'inherit' }}>{bar}</span>{' '}
          {portal.earnedXp}/{portal.maxXp} XP{' '}
          <span className="tm-text--dim">·</span>{' '}
          {portal.headroomXp > 0
            ? <span className="tm-feedback-correct">+{portal.headroomXp} headroom</span>
            : <span className="tm-text--dim">mastered</span>}
        </span>
      </button>
    </div>
  );
}
